// Public PDF landing page — accessed via /info/:slug, no auth required.
// Branded Creative Mojo viewer with a single big yellow CTA that 302's
// straight into a fresh signed R2 download URL.
//
// The optional `?t=<send-id>` query param ties this visit to the email
// that drove it, so the admin's Activity Timeline picks the click up.
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";
import axios from "axios";
import DOMPurify from "dompurify";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function InfoLandingPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t");
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError("");
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/api/public/landing/${encodeURIComponent(slug)}`,
          { params: token ? { t: token } : {} },
        );
        if (!cancelled) setPage(data);
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.status === 404
            ? "This page no longer exists. Please contact us if you reached this link from an email."
            : (e?.response?.data?.detail || "Could not load this page."));
        }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [slug, token]);

  const downloadUrl = `${BACKEND_URL}/api/public/landing/${encodeURIComponent(slug)}/download${token ? `?t=${encodeURIComponent(token)}` : ""}`;

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col" data-testid="info-landing-page">
      <header className="px-6 sm:px-10 pt-10 sm:pt-14 pb-4 flex justify-center">
        <img
          src="/brand/creative-mojo-logo.png"
          alt="Creative Mojo"
          className="max-w-[240px] h-auto"
          data-testid="info-landing-logo"
        />
      </header>

      <main className="flex-1 px-4 sm:px-6 py-6 flex justify-center">
        <div className="w-full max-w-2xl sm:max-w-4xl">
          {loading && (
            <div className="bg-white rounded-2xl border border-stone-200 p-10 text-center text-stone-500" data-testid="info-landing-loading">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" />
              Loading…
            </div>
          )}
          {error && !loading && (
            <div className="bg-white rounded-2xl border border-rose-200 p-10 text-center text-rose-800" data-testid="info-landing-error">
              <AlertCircle className="w-7 h-7 mx-auto mb-3" />
              <div className="font-semibold mb-1">Page unavailable</div>
              <div className="text-sm text-rose-700">{error}</div>
            </div>
          )}
          {page && !loading && (
            <article className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
              <div className="px-6 sm:px-10 pt-8 sm:pt-10 pb-6">
                <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight leading-tight" data-testid="info-landing-title">
                  {page.title}
                </h1>
                {page.intro_html ? (
                  <div
                    className="prose prose-stone mt-4 text-stone-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(page.intro_html) }}
                    data-testid="info-landing-intro"
                  />
                ) : null}
              </div>

              <div className="px-6 sm:px-10 pb-10 pt-4 bg-stone-50 border-t border-stone-100">
                {page.has_file ? (
                  // Two branded graphics side-by-side per the visual:
                  // brochure spread on the left, big yellow "DOWNLOAD
                  // THE INFO PACK" button on the right.  Both are the
                  // same anchor target — clicking either downloads the
                  // PDF.  Stacks vertically on mobile so the brochure
                  // sits above the CTA on narrow screens.
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-8">
                    <a
                      href={downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`Download ${page.file_name || "PDF"}`}
                      data-testid="info-landing-brochure"
                      className="shrink-0 transition-transform hover:scale-[1.02]"
                    >
                      <img
                        src="/brand/franchise-brochure.png"
                        alt="Creative Mojo Franchise Pack preview"
                        className="w-full max-w-[380px] h-auto"
                      />
                    </a>
                    <a
                      href={downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`Download ${page.file_name || "PDF"}`}
                      data-testid="info-landing-download-btn"
                      aria-label={page.cta_label || "Download the info pack"}
                      className="shrink-0 transition-transform hover:scale-[1.03]"
                    >
                      <img
                        src="/brand/franchise-download-button.png"
                        alt={page.cta_label || "Download the info pack"}
                        className="w-full max-w-[280px] h-auto"
                      />
                    </a>
                  </div>
                ) : (
                  <div className="text-sm text-stone-500 italic">
                    No file attached to this page yet.
                  </div>
                )}
              </div>
            </article>
          )}
        </div>
      </main>

      <footer className="px-6 py-6 text-center text-[11px] text-stone-400">
        © {new Date().getFullYear()} Creative Mojo Ltd · Registered in England &amp; Wales No. 10261882
      </footer>
    </div>
  );
}
